import { useCallback, useReducer, useRef } from 'react';
import useUnmount from './use-unmount';

interface MutationInnerProps<T> {
  isLoading: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  data?: T;
  error?: unknown;
}

const CANCEL_MARK = 'cancel';

export interface MutationProps<T, P> {
  asyncFn: ((variables: P) => Promise<T>) | (() => Promise<T>);
  handlers: {
    onSuccess?: (data: T, variables: P) => void;
    onError?: (e: unknown, variables: P) => void;
    onSettled?: (data?: T, e?: unknown, variables?: P) => void;
  };
}

export type RunType<T, P> = (variables?: P) => Promise<T>;

enum MutationAction {
  RESET = 'reset',
  SET_DATA = 'setData',
  SET_ERROR = 'setError',
  SET_LOADING = 'setLoading',
}

const reducer = <T>(
  pre: MutationInnerProps<T>,
  action: { type: MutationAction; payload?: any },
): MutationInnerProps<T> => {
  switch (action.type) {
    case MutationAction.SET_LOADING:
      return {
        isLoading: true,
        isSuccess: undefined,
        isError: undefined,
        data: undefined,
        error: undefined,
      };
    case MutationAction.RESET:
      return {
        isLoading: false,
        isSuccess: undefined,
        isError: undefined,
        data: undefined,
        error: undefined,
      };
    case MutationAction.SET_DATA:
      return {
        ...pre,
        data: action.payload,
        error: undefined,
        isLoading: false,
        isSuccess: true,
        isError: false,
      };
    case MutationAction.SET_ERROR:
      return {
        ...pre,
        error: action.payload,
        data: undefined,
        isLoading: false,
        isSuccess: false,
        isError: true,
      };
    default:
      return { ...pre, isLoading: false };
  }
};

const useMutation = <T, P>(
  asyncFn: MutationProps<T, P>['asyncFn'],
  handlers?: MutationProps<T, P>['handlers'],
) => {
  const { onSuccess, onError, onSettled } = handlers || {};
  const isActiveFlag = useRef(true);
  const cancelRef = useRef<any>(null);
  const dataRef = useRef<Awaited<T>>();
  const errorRef = useRef<any>();

  useUnmount(() => {
    isActiveFlag.current = false;
  });

  const [state, dispatch] = useReducer<
    (
      pre: MutationInnerProps<T>,
      action: {
        type: MutationAction;
        payload?: any;
      },
    ) => MutationInnerProps<T>
  >(reducer, {
    isLoading: false,
    isSuccess: undefined,
    isError: undefined,
    data: undefined,
    error: undefined,
  });

  const run: RunType<T, P> = (variables?: P) => {
    // reset state at the beginning of a new run.
    dispatch({ type: MutationAction.SET_LOADING });
    dataRef.current = undefined;
    errorRef.current = undefined;
    const cancelPromise: Promise<typeof CANCEL_MARK> = new Promise(resolve => {
      cancelRef.current = resolve;
    });
    return Promise.race([asyncFn(variables ?? ({} as P)), cancelPromise])
      .then(data => {
        if (!isActiveFlag.current || data === CANCEL_MARK) {
          return undefined;
        }
        dispatch({ type: MutationAction.SET_DATA, payload: data });
        onSuccess?.(data, variables ?? ({} as P));
        dataRef.current = data;
        return data;
      })
      .catch(error => {
        if (!isActiveFlag.current) {
          return undefined;
        }
        dispatch({ type: MutationAction.SET_ERROR, payload: error });
        onError?.(error, variables ?? ({} as P));
        errorRef.current = error;
        return error;
      })
      .finally(() => {
        if (!isActiveFlag.current) {
          return;
        }
        onSettled?.(dataRef.current, errorRef.current, variables);
      });
  };

  const reset = useCallback(() => {
    dispatch({ type: MutationAction.RESET });
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current?.(CANCEL_MARK);
  }, []);

  return {
    mutateAsync: run,
    reset,
    cancel,
    ...state,
  };
};

export default useMutation;
